import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { Globals } from 'app/_globals';
import { CompetencyTypesService } from 'app/_services/competency-types.service';
import { ContractorService } from 'app/_services/contractor.service';
import { FileService } from 'app/_services/file.service';
import { NotificationService } from 'app/_services/notification.service';
import { environment } from 'environments/environment';
import { TrainingCourse } from 'app/_models/training/training-course.model';
import { TrainingService } from 'app/_services/training.service';
import DateTimeUtils from 'app/_utils/datetime.utils';

@Component({
  selector: 'contractor-certificates',
  templateUrl: './certificates.component.html',
})
export class ContractorCertificatesComponent implements OnInit {
  @ViewChild('fileInput') fileInput: ElementRef;

  private routeRole: any;
  public tabitems: any[] = [];
  public errorAlerts;

  public competencyTypes: any[] = [];
  public saveInProgress = false;

  public showAddCertificateTypeModal = false;
  public addCertificateForm: FormGroup;

  public selectedDocumentsArray: any[] = [];
  public areFilesAdded = false;

  public minDateValue = new Date();
  private currentYear = new Date().getFullYear();
  public datepickerYearRange = this.currentYear + ':' + (this.currentYear + 10);

  public pageTitle = 'Company Certificates';

  // pagination
  pageSizes: any = environment.pageSizes;
  pagination: any;

  private selectedContractorId: any;
  private isAdmin: boolean;
  public competencyToUpload: any;
  private selectedCompetencySiteOwnerId: any;
  public previousFiles = [];
  public siteOwnersList = [];

  public extraCompetencyInfo: { [competencyId: number]: string } = {};

  constructor(
    private contractorService: ContractorService,
    private competencyService: CompetencyTypesService,
    private notificationService: NotificationService,
    private trainingService: TrainingService,
    private activatedRoute: ActivatedRoute,
    private fileService: FileService,
    globals: Globals,
    fb: FormBuilder,
  ) {
    this.routeRole = globals.user.routeRole;

    this.tabitems = [
      { path: '/' + this.routeRole + '/company/details', title: 'Details' },
      { path: '/' + this.routeRole + '/company/contacts', title: 'Contacts' },
      { path: '/' + this.routeRole + '/company/certificates', title: 'Certificates' },
    ];

    if (this.routeRole === 'admin' || this.routeRole == 'support') {
      this.isAdmin = true;
      this.activatedRoute.params.subscribe((params: Params) => {
        this.selectedContractorId = params.id;
      });

      this.tabitems = [
        { path: '/' + this.routeRole + '/contractors/', title: 'All Contractors', class: 'back-link' },
        { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/details', title: 'Details' },
        { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/workers', title: 'Workers' },
        { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/certificates', title: 'Certificates' },
        { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/contacts', title: 'Contacts' },
      ];
    }
    // TODO: OEM Procedures is intentionally hidden from the UI for now.
    // Keep the feature code in place until the product/backend direction for this area is defined.
    // else if(globals.contractor.oem){
    //     this.tabitems.push({ path: '/' + this.routeRole + '/company/procedures', title: 'Procedures' });
    // }

    this.addCertificateForm = fb.group({
      competencyType: new FormControl(null, [Validators.required]),
      expires: new FormControl(null, [Validators.required]),
      files: new FormControl(null)
    });

    this.resetPagination();
  }

  ngOnInit() {
    this.getAllCertificateTypes();
    this.getSelectedContractor();
  }

  private getSelectedContractor() {
    let request;
    if (this.isAdmin) {
      request = this.contractorService.getContractorById(this.selectedContractorId);
    } else {
      request = this.contractorService.getContractorForCurrentUser();
    }

    request.subscribe((data: any) => {
      this.selectedContractorId = data.id;
      if (this.isAdmin) {
        this.pageTitle = data.name;
      }
      this.siteOwnersList = data.maintenanceSiteOwners
          .map(mso => ({
            siteOwnerId: mso.siteOwnerUser.skylarkUserId,
            siteOwnerName: mso.siteOwnerUser.name,
            isPowerstationOwner: !!mso.siteOwnerUser.mainSiteId, //only powerstation owners have this field valued
            powerStationId: mso.siteOwnerUser.mainSiteId || null, //only powerstation owners have this field valued
            competencies: mso.maintenanceSiteOwnerCompetencyTypes
                .filter(c => !c.deleted)
                .map(c => this.mapNearExpiry(c))
                .sort((a, b) => a.competencyType.value.localeCompare(b.competencyType.value)),
            outageAcceptances: data.userCourses
                .filter(uc => uc.course.siteOwner.id === mso.siteOwnerUser.skylarkUserId)
                .filter(uc => !uc.deleted)
                .map(uc => ({
                  course: new TrainingCourse().deserialize(uc.course),
                  id: uc.id,
                  expiryDate: uc.expiryDate,
                  courseProgress: uc.courseProgress,
                  lastModifiedDate: uc.lastModifiedDate,
                }))
                .sort((a, b) => new Date(b.expiryDate).getTime() - new Date(a.expiryDate).getTime()) // Sort by expiryDate descending
          }))
          .sort(this.sortSiteOwnersByAnyCompetenciesAndThenName);
          // TODO: OEM Procedures is intentionally hidden from the UI for now.
          // Keep the feature code in place until the product/backend direction for this area is defined.
          // if (data.oem && this.isAdmin) {
          //   this.tabitems.push( { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/procedures', title: 'Procedures' },);
          // }

          // client Huntstown specifically wants extra text displayed next to certain competencies
          // only for powerstation to populate the extraCompetencyInfo
          this.siteOwnersList.forEach((siteOwner) => {
            const isHuntstown = siteOwner.isPowerstationOwner && siteOwner.powerStationId === environment.HUNTSTOWN_ID
            if (isHuntstown) {
              this.extraCompetencyInfo = environment.CONTRACTOR_COMPETENCY_ID_TO_EXTRA_INFO_TEXT;
            }
          });
    });
  }

  public uploadCertificate(competency) {
    this.selectedCompetencySiteOwnerId = competency.id;

    this.competencyToUpload =  {
      label: competency.competencyType.value,
      value: competency.competencyType.id,
    }

    this.previousFiles = competency.files;

    this.areFilesAdded = false;
    this.resetFileInput();

    this.addCertificateForm.reset(  {competencyType: {value: '', disabled: true},} );
    this.addCertificateForm.patchValue({
      competencyType: this.competencyToUpload,
    });
    this.addCertificateForm.get('competencyType').disable();

    this.showAddCertificateTypeModal = true;
  }

  private getAllCertificateTypes() {
    this.competencyService.getAllCompetencyTypesByType('POWERSTATION').subscribe((data: any) => {
      this.competencyTypes.push({ label: 'Select Type', value: null });
      data.forEach((element) => {
        const item = {
          label: element.value,
          value: element.id,
        };
        this.competencyTypes.push(item);
      });
    });
  }

  public showAddCertificateModal() {
    this.resetForm();
    this.resetFileInput();

    this.showAddCertificateTypeModal = true;
  }

  public onFileChange(event) {
    for (let i = 0; i < event.target.files.length; i++) {
      const file = event.target.files[i];
      this.selectedDocumentsArray.push(file);
    }
    this.areFilesAdded = (this.selectedDocumentsArray.length > 0);
  }

  private resetFileInput() {
    //Reset state of file to avoid data leak
    this.selectedDocumentsArray = [];
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  public async onAddCertificateFormSubmit() {
    this.saveInProgress = true;
    const values = this.addCertificateForm.value;

    const body = {
      expiryDate: DateTimeUtils.timelessDateAsUTCDate(values.expires).toISOString(),
    };

    this.fileService.deleteAllContractorCompetencyFiles(this.selectedCompetencySiteOwnerId).concatMap(() => {
      return this.contractorService.createContractorCompetency(this.selectedCompetencySiteOwnerId, body, this.selectedDocumentsArray);
    }).subscribe((data: any) => {
      this.showAddCertificateTypeModal = false;
      this.saveInProgress = false;
      this.getSelectedContractor();
      this.notificationService.notify('success', 'Certificate Added');
    }, (error: any) => {
      this.saveInProgress = false;
      if (error.message === 'Infected') {
        this.notificationService.notify('danger', 'Not Added', 'File is infected with malware!');
      } else if (error.message === 'EmbeddedScript') {
        this.notificationService.notify('danger', 'Not Added', 'File has embedded script - please submit file to Skylark for analysis');
      } else {
        this.notificationService.notify('danger', 'Not Added', 'There was an issue saving this certificate');
      }
    });
  }

  // Handle Pagination
  public paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.getSelectedContractor();
  }

  private resetPagination() {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };
  }

  public async noDocumentToUploadChange(isChecked: boolean) {
    this.addCertificateForm.get('files').reset();
    this.selectedDocumentsArray = [];
    this.areFilesAdded = isChecked;
    if (isChecked) {
      const image = await this.contractorService.getSkylarkCompetencyImage()
      this.selectedDocumentsArray = [image];
    }
  }

  private resetForm() {
    this.areFilesAdded = false;
    this.selectedDocumentsArray = [];
    this.addCertificateForm.reset();
    this.addCertificateForm.get('competencyType').enable();
  }

  public viewDocument(fileName) {
    this.contractorService.showFile(environment.apiUrl + 'api/ext-file/mainteanceSiteOwnerCompetencyFiles', fileName);
  }

  private mapNearExpiry(cert: any): any {

    if(cert.expiryDate){
      let weekLater = new Date();
      weekLater.setDate(weekLater.getDate() + 7);
      if(new Date(cert.expiryDate) < weekLater && cert.status === "APPROVED"){
        cert.nearExpiry = true;
      }
    }
    return cert;
  }

  private sortSiteOwnersByAnyCompetenciesAndThenName(a, b): number {
    if (a.competencies.length === 0 && b.competencies.length > 0) {
      return 1;
    }
    if (a.competencies.length > 0 && b.competencies.length === 0) {
      return -1;
    }

    return a.siteOwnerName.localeCompare(b.siteOwnerName);
  }
}
